import { useQuery } from '@tanstack/react-query';

import { useHttpClient } from '~/hooks/useHttpClient';
import { ScorecardType, StudentWithCount, SessionStatus, StudentWithAttendance } from '~/services/types';
import { determineRetryAndMaybeLog } from '~/utils/errors';
import { createLogger } from '~/utils/logging';

import { useReviewParams } from './useReviewParams';

const log = createLogger('useReviewScorecardsQuery');

export const QUERY_KEY = 'reviewScorecards';

export type ReviewScorecardsResponse = {
  // These are (name, score) pairs for praised, warned, and absent students.
  scorecards: Record<ScorecardType, StudentWithCount[] | StudentWithAttendance[]>;
  session?: {
    status: SessionStatus;
  };
};

export function useReviewScorecards({ date, periodId }: { date: Date; periodId: string }) {
  const httpClient = useHttpClient();
  const params = useReviewParams({ date, periodId });

  const paramsQueryStr = params.toQueryString();

  const { data, error, status } = useQuery<null | ReviewScorecardsResponse>({
    queryKey: [QUERY_KEY, paramsQueryStr],
    queryFn: async () => {
      log.debug('Fetching review scorecards', {
        day: params.day,
        periodId: params.periodId,
        timeZone: params.timeZone ?? 'unknown',
      });
      const { scorecards, session } = await httpClient.get<ReviewScorecardsResponse>(
        `/review/getScorecards?${paramsQueryStr}`,
      );
      log.info('Fetched review scorecards successfully', {
        scorecardCount: Object.keys(scorecards).length,
      });
      return { scorecards, session };
    },
    retry: (failureCount, error) =>
      determineRetryAndMaybeLog({ operation: 'fetch review scorecards', failureCount, error, log }),
    enabled: httpClient.isAuthenticated,
  });

  return { data, error, status };
}
